/**
 * Modalidades de uma assembleia da Servopa e o lance típico — só dados, sem
 * React (mesmo motivo de `filtrosAvancados.ts`).
 *
 * A Servopa publica por assembleia: SORTEIO, LANCE LIVRE (o livre COM
 * embutido), SEM EMBUTIDO (o livre sem embutido — leilão separado, a regra do
 * grupo diz "LANCE LIVRE SEM EMBUTIDO" e "LANCE LIVRE COM EMBUTIDO"), FIXO e
 * FIDELIDADE — desde 15/10/2026 em dois leilões, "FIDELIDADE 30%" e
 * "FIDELIDADE 15%". Nunca "LANCE FIXO". O
 * `menor_lance` é o menor lance VENCEDOR da modalidade; vem 0 quando ninguém
 * foi contemplado. A Servopa não publica o maior lance (migration 122).
 */

export interface ModalidadeAssembleia {
  modalidade: string | null;
  contemplados: number | null;
  menor_lance: number | string | null;
}

export interface AssembleiaComModalidades {
  data: string | null;
  servopa_assembly_modalidades: ModalidadeAssembleia[] | null;
}

/** Ordem das colunas: a mais disputada primeiro. */
const ORDEM: string[] = ['LANCE LIVRE', 'SEM EMBUTIDO', 'FIXO', 'FIDELIDADE', 'FIDELIDADE 30%', 'FIDELIDADE 15%'];

const ROTULO: Record<string, string> = {
  'LANCE LIVRE': 'Livre com embutido',
  'SEM EMBUTIDO': 'Livre sem embutido',
  FIXO: 'Lance fixo',
  FIDELIDADE: 'Fidelidade',
  'FIDELIDADE 30%': 'Fidelidade 30%',
  'FIDELIDADE 15%': 'Fidelidade 15%',
};

export const rotuloDaModalidade = (modalidade: string): string => ROTULO[modalidade] ?? modalidade;

const nome = (m: ModalidadeAssembleia) => (m.modalidade ?? '').trim().toUpperCase();

/** As modalidades de lance que aparecem no histórico deste grupo, na ordem fixa. */
export const modalidadesDeLance = (assembleias: AssembleiaComModalidades[]): string[] => {
  const presentes = new Set<string>();
  for (const a of assembleias) {
    for (const m of a.servopa_assembly_modalidades ?? []) {
      const n = nome(m);
      if (n && n !== 'SORTEIO') presentes.add(n);
    }
  }
  const conhecidas = ORDEM.filter((n) => presentes.has(n));
  const outras = [...presentes].filter((n) => !ORDEM.includes(n)).sort();
  return [...conhecidas, ...outras];
};

export const modalidadeDa = <M extends ModalidadeAssembleia>(
  assembleia: { servopa_assembly_modalidades: M[] | null } | null | undefined,
  modalidade: string,
): M | null => assembleia?.servopa_assembly_modalidades?.find((m) => nome(m) === modalidade) ?? null;

/** O lance vencedor, só quando houve contemplação (senão o 0 diria "contempla com 0%"). */
export const lanceVencedor = (m: ModalidadeAssembleia | null): number | null => {
  if (!m || !((m.contemplados ?? 0) > 0) || m.menor_lance == null) return null;
  const pct = Number(m.menor_lance);
  return Number.isFinite(pct) && pct > 0 ? pct : null;
};

/** Janela e amostra do lance típico — as mesmas da view (migration 252). */
export const ASSEMBLEIAS_LANCE_TIPICO = 3;
export const MIN_LANCES_TIPICO = 2;

/**
 * Lance típico de um dos leilões livres (com embutido = 'LANCE LIVRE', sem
 * embutido = 'SEM EMBUTIDO'): média do lance vencedor nas 3 últimas
 * assembleias do grupo ("2 a 3 meses antes da assembleia atual"), sem 100%
 * (cota cancelada/desempate). Com menos de 2 lances nelas, `media` é null —
 * um lance só não é típico. Conta pelas assembleias, não pelo calendário: o
 * atraso do sync não apaga o número.
 */
export const lanceTipico = (
  assembleias: (AssembleiaComModalidades & { assembleia?: number | null })[],
  modalidade: 'LANCE LIVRE' | 'SEM EMBUTIDO' = 'LANCE LIVRE',
): { media: number | null; amostras: number } => {
  const lances = ultimasAssembleias(assembleias)
    .map((a) => lanceVencedor(modalidadeDa(a, modalidade)))
    .filter((pct): pct is number => pct != null && pct < 100);
  const media =
    lances.length >= MIN_LANCES_TIPICO ? lances.reduce((s, v) => s + v, 0) / lances.length : null;
  return { media, amostras: lances.length };
};

/** As 3 últimas assembleias com data, da mais recente para a mais antiga. */
const ultimasAssembleias = <A extends AssembleiaComModalidades & { assembleia?: number | null }>(assembleias: A[]): A[] =>
  assembleias
    .filter((a) => a.data != null)
    .sort((a, b) => (b.data! < a.data! ? -1 : b.data! > a.data! ? 1 : (b.assembleia ?? 0) - (a.assembleia ?? 0)))
    .slice(0, ASSEMBLEIAS_LANCE_TIPICO);

/**
 * Contemplados por assembleia nas 3 últimas (sorteio e todas as modalidades
 * de lance) — a mesma conta da view (contemplados_mes_recente, migration 252)
 * que dá o selo "Contempla rápido" na lista. null sem assembleia com data.
 */
export const contempladosPorAssembleiaRecente = (
  assembleias: (AssembleiaComModalidades & { assembleia?: number | null })[],
): number | null => {
  const ultimas = ultimasAssembleias(assembleias);
  if (ultimas.length === 0) return null;
  const total = ultimas.reduce(
    (s, a) => s + (a.servopa_assembly_modalidades ?? []).reduce((t, m) => t + (m.contemplados ?? 0), 0),
    0,
  );
  return total / ultimas.length;
};
